
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingUp, BarChart3, PiggyBank } from "lucide-react";

/**
 * BetaModal component that displays information about the beta investment features
 * Shows when users first access the invest section to inform them about the demo nature
 */
interface BetaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BetaModal = ({ isOpen, onClose }: BetaModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <DialogTitle className="text-2xl font-bold">Welcome to Invest</DialogTitle>
            <Badge variant="secondary" className="bg-orange-100 text-orange-800">
              BETA
            </Badge>
          </div>
          <DialogDescription className="text-base space-y-4">
            <div className="flex items-center justify-center gap-2 text-orange-600 mb-3">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-semibold">This is a demo version</span>
            </div>
            
            <p className="text-gray-600">
              You're now in the investment section of Centse. This is currently a beta version 
              with simulated data and features designed to help you learn about investing.
            </p>
            
            <div className="grid grid-cols-3 gap-4 my-6">
              <div className="text-center">
                <div className="p-3 bg-blue-100 rounded-full w-fit mx-auto mb-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-xs text-gray-600">Market Simulator</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-green-100 rounded-full w-fit mx-auto mb-2">
                  <BarChart3 className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-xs text-gray-600">Portfolio Tracking</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-purple-100 rounded-full w-fit mx-auto mb-2">
                  <PiggyBank className="h-5 w-5 text-purple-600" />
                </div>
                <p className="text-xs text-gray-600">Savings Goals</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-500">
              All market data, portfolios, and transactions are simulated for educational purposes only.
            </p>
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex justify-center mt-6">
          <Button onClick={onClose} className="w-full">
            Get Started
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BetaModal;
